import React, { useState, useEffect, useMemo } from 'react';
import MatchCard from '../../MatchCard/MatchCard';
import './styles.scss';
import chevronarriba from '../../../assets/chevronarriba.png';
import chevronabajo from '../../../assets/chevronabajo.png';
import smallShield from '../../../assets/smallShield.png';
import { TRANSLATION_KEYS } from './constants';
import { useTranslation } from 'hooks/useTranslation';
import { formatMatchTimeByRegion } from '../../../../utils/formatMatchTime';
import { getRegion } from 'utils/Region';
import { useMundialMetrics } from '../../../../hooks/useMundialMetrics';
import { INTERACTION_TYPES } from '../../../../hooks/useMundialMetrics/constants';
import { ACTIONABLE_MATCH_STATUSES } from '../../../../hooks/useMundialMetrics/utils/buildMatchValues';
import { useMundialScrollTracker } from '../../../../hooks/useMundialScrollTracker';
import { getVisibleMatchNames } from '../../../utils/getVisibleMatchNames';

export default function GroupStage({
  stageData,
  mobileMenuOpen,
  onMobileGroupMenuOpenChange,
  activeGroup,
  onGroupChange,
  onMatchCardClick,
}) {
  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(
    () => translationMapper(TRANSLATION_KEYS),
    [],
  );
  const region = getRegion();

  const { trackSelectContent } = useMundialMetrics();
  const [uncontrolledMenuOpen, setUncontrolledMenuOpen] = useState(true);
  const isMenuControlled = mobileMenuOpen !== undefined;
  const isMobileMenuOpen = isMenuControlled
    ? mobileMenuOpen
    : uncontrolledMenuOpen;
  const setMobileMenuOpen = next => {
    const value = typeof next === 'function' ? next(isMobileMenuOpen) : next;
    if (isMenuControlled) {
      onMobileGroupMenuOpenChange?.(value);
      return;
    }
    setUncontrolledMenuOpen(value);
    onMobileGroupMenuOpenChange?.(value);
  };

  const rounds = stageData?.data?.rounds || [];

  const getShortName = r =>
    r.roundshortname || r.roundName?.split(' ').pop() || '';

  const activeRound = rounds.find(r => getShortName(r) === activeGroup);

  useMundialScrollTracker({
    pageTitleSegments: ['fase de grupos', `grupo ${activeGroup.toLowerCase()}`],
    contentSubsection: 'fase de grupos',
    moduloName: `grupo ${activeGroup.toLowerCase()}`,
    getVisibleContentIds: getVisibleMatchNames,
  });

  const groupedMatches = useMemo(
    () => {
      if (!activeRound?.matches) return [];
      const pairs = [];
      for (let i = 0; i < activeRound.matches.length; i += 2) {
        const chunk = activeRound.matches.slice(i, i + 2);
        const dateObj = new Date(chunk[0].scheduledAt);
        const day = dateObj.getDate();
        const month = dateObj
          .toLocaleString('es-MX', { month: 'long' })
          .toUpperCase();
        pairs.push({
          label: `${translatedTexts['TEXTO_' + (pairs.length + 1)]}`,
          date: `${day} DE ${month}`,
          matches: chunk,
          isLast: i + 2 >= activeRound.matches.length,
        });
      }
      return pairs;
    },
    [activeRound],
  );

  const setActiveGroup = g => {
    const moduloName = `grupo ${g.toLowerCase()}`;
    trackSelectContent({
      pageTitleSegments: ['fase de grupos', moduloName],
      pageTitle: moduloName,
      contentSubsection: 'fase de grupos',
      moduloName,
      interactionType: INTERACTION_TYPES.GROUP_TABS,
    });
    onGroupChange?.(g);
  };

  const handleMatchClick = match => {
    onMatchCardClick?.(match.matchId);
    if (!ACTIONABLE_MATCH_STATUSES.includes(match.status)) return;
    const moduloName = `grupo ${activeGroup.toLowerCase()}`;
    const matchName = `${match.homeTeam?.name || ''} vs ${match.awayTeam
      ?.name || ''}`;
    trackSelectContent({
      pageTitleSegments: ['fase de grupos', moduloName],
      pageTitle: matchName,
      contentSubsection: 'fase de grupos',
      moduloName,
      interactionType: INTERACTION_TYPES.CARD_SELECT,
      match,
    });
  };

  useEffect(
    () => {
      if (isMenuControlled) return undefined;
      onMobileGroupMenuOpenChange?.(isMobileMenuOpen);
      return undefined;
    },
    [isMenuControlled, isMobileMenuOpen, onMobileGroupMenuOpenChange],
  );

  return (
    <div className="group-stage">
      <div className="group-stage__tabs">
        <div
          className="group-stage__mobile-tabs"
          aria-label="Selector de grupo (mobile)"
        >
          <div className="group-stage__mobile-active">Grupo {activeGroup}</div>
          <button
            type="button"
            className="group-stage__mobile-chevron"
            onClick={() => setMobileMenuOpen(v => !v)}
            aria-expanded={isMobileMenuOpen}
            aria-label={
              isMobileMenuOpen
                ? 'Cerrar selector de grupo'
                : 'Abrir selector de grupo'
            }
          >
            <img
              style={{ width: 12, height: 7 }}
              src={isMobileMenuOpen ? chevronarriba : chevronabajo}
            />
          </button>
        </div>

        {isMobileMenuOpen && (
          <div
            className="group-stage__mobile-menu"
            role="menu"
            aria-label="Grupos"
          >
            {rounds.map(r => (
              <button
                key={r.roundCode}
                type="button"
                role="menuitem"
                className={`group-stage__mobile-option ${
                  activeGroup === getShortName(r)
                    ? 'group-stage__mobile-option--active'
                    : ''
                }`}
                onClick={() => setActiveGroup(getShortName(r))}
              >
                {getShortName(r)}
              </button>
            ))}
          </div>
        )}

        {rounds.map(r => (
          <button
            key={r.roundCode}
            onClick={() => setActiveGroup(getShortName(r))}
            className={`group-stage__tab ${
              activeGroup === getShortName(r) ? 'group-stage__tab--active' : ''
            }`}
          >
            {activeGroup === getShortName(r) ? r.roundName : getShortName(r)}
          </button>
        ))}
      </div>

      <div className="group-stage__grid">
        {groupedMatches.map(({ label, date, matches, isLast }, gi) => (
          <div key={gi}>
            <div className="group-stage__column-header">
              <div className="group-stage__fecha-label">{label}</div>
              <div className="group-stage__fecha-date-mobile">{date}</div>
            </div>
            <div
              className={`group-stage__matches ${
                !isLast ? 'group-stage__matches--bordered' : ''
              }`}
            >
              {matches.map((m, mi) => {
                const dateObj = new Date(m.scheduledAt);
                const day = dateObj.getDate();
                const month = dateObj
                  .toLocaleString('es-MX', { month: 'long' })
                  .toUpperCase();
                const dateStr = `${day} DE ${month}`;
                const timeLabel = formatMatchTimeByRegion(m, {
                  includeDate: false,
                });

                let showDate = true;
                if (mi > 0) {
                  const prevDate = new Date(matches[mi - 1].scheduledAt);
                  showDate =
                    prevDate.getDate() !== dateObj.getDate() ||
                    prevDate.getMonth() !== dateObj.getMonth();
                }

                return (
                  <div key={m.matchId} className="group-stage__match-wrapper">
                    {showDate && (
                      <div className="group-stage__match-date">{dateStr}</div>
                    )}
                    <MatchCard
                      time={timeLabel}
                      teamA={m.homeTeam.name}
                      teamB={m.awayTeam.name}
                      shortNameA={m.homeTeam.shortName}
                      shortNameB={m.awayTeam.shortName}
                      flagUrlA={m.homeTeam.flagUrl}
                      flagUrlB={m.awayTeam.flagUrl}
                      stadium={`${m.venue.name}, ${m.venue.city}`}
                      status={m.status}
                      score={m.score}
                      matchMinute={m.matchProgress?.minute}
                      isBroadcastAvailable={m.broadcast?.isAvailable}
                      abbreviateTeamNamesOnMobile
                      noFlagPlaceholderSrc={smallShield}
                      onClick={
                        onMatchCardClick ? () => handleMatchClick(m) : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
